import { useEffect, useLayoutEffect, useRef } from 'react'
import * as THREE from 'three'
import { HAL } from './indeling'
import { ZON } from './daglicht'
import { useLicht, useProfiel } from '../scene/store'
import { PROFIEL_DETAIL } from '../scene/config'

/**
 * Het zonlicht, gedeeld met de lichtschachten: die lezen zijn schaduwkaart
 * om te weten waar in de lucht de zon komt. Een ref op moduleniveau om dezelfde
 * reden als `focusPunt` in `CameraRig.tsx`.
 */
export const zonLicht: { current: THREE.DirectionalLight | null } = { current: null }

/**
 * De lichtbronnen. De zichtbare armaturen staan in `Armaturen.tsx`, het dak en
 * de lichtband in `Dak.tsx`; hier staat alleen wat de materialen verlicht.
 *
 * ## Drie soorten licht, zoals in een echte hal
 *
 *  1. **De zon**, door de daklichten. Warm, fel, met schaduw — en omdat het dak
 *     meedoet in de schaduwkaart valt hij alleen in vlekken naar binnen.
 *  2. **De armaturen**, als twee schaduwloze vullichten schuin van boven, één
 *     per kant van het gangpad. Eén licht recht van boven zou de vloer en de
 *     bovenkant van elke doos verlichten en de stellingfronten zwart laten —
 *     terwijl die fronten juist zijn wat je in een gangpad ziet.
 *  3. **Het omgevingslicht**: hemisfeer plus een lage ambient, koel. Dit is het
 *     licht dat van alle wanden terugkaatst en wat de schaduwen niet zwart maakt.
 */
export function Licht() {
  const licht = useLicht()
  const kaart = PROFIEL_DETAIL[useProfiel()].schaduwkaart
  const zon = useRef<THREE.DirectionalLight>(null)

  useLayoutEffect(() => {
    zonLicht.current = zon.current
    return () => { zonLicht.current = null }
  }, [])

  // Het doel moet in de scène hangen, anders rekent three met een doel dat
  // nooit een wereldmatrix krijgt en wijst de schaduwcamera de verkeerde kant op.
  useEffect(() => {
    const z = zon.current
    if (!z) return
    z.target.position.set(-2, 0, 0)
    z.parent?.add(z.target)
    return () => { z.target.parent?.remove(z.target) }
  }, [])

  // three maakt de schaduwkaart één keer aan en kijkt daarna niet meer naar
  // mapSize: een profielwissel zonder dit verandert alleen het getal. Weggooien
  // laat de renderer hem bij het volgende beeld op de nieuwe maat bouwen.
  useEffect(() => {
    const z = zon.current
    if (!z) return
    z.shadow.mapSize.set(kaart, kaart)
    z.shadow.map?.dispose()
    z.shadow.map = null
  }, [kaart])

  const zonPositie = ZON.clone().multiplyScalar(60).add(new THREE.Vector3(-2, 0, 0))

  return (
    <group>
      <ambientLight intensity={licht.ambient} color="#8f9bb3" />
      <hemisphereLight args={['#6b7690', '#15171b', licht.ambient * 1.6]} />

      <directionalLight
        ref={zon}
        position={zonPositie.toArray()}
        intensity={licht.daklichten ? licht.zonSterkte : 0}
        color={licht.zonKleur}
        castShadow={licht.schaduwen}
        // De schaduwcamera moet de hele hal dekken, niet alleen de stellingen:
        // buiten haar bereik rekent three alles als belicht, en dan valt er
        // zon door het dak heen op de vloer langs de wanden.
        shadow-mapSize={[kaart, kaart]}
        shadow-camera-left={-40}
        shadow-camera-right={40}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
        shadow-camera-near={20}
        shadow-camera-far={110}
        shadow-bias={-0.0004}
        shadow-normalBias={0.03}
      />

      {/* Vullicht van de armaturen, per kant van het gangpad. */}
      <directionalLight position={[0, 20, 10]} intensity={licht.vulSterkte * 0.55 * licht.armatuurSterkte} color={licht.armatuurKleur} />
      <directionalLight position={[0, 20, -10]} intensity={licht.vulSterkte * 0.55 * licht.armatuurSterkte} color={licht.armatuurKleur} />
      {/* En een zwakke van de kopse kant, zodat de stellingkoppen niet dood zijn. */}
      <directionalLight position={[30, 12, 4]} intensity={licht.vulSterkte * 0.18} color="#b9c6de" />

      {/* Merkgloed vanaf de kopse kant — geeft diepte aan de gangpaden. */}
      <pointLight
        position={[-28, 6, 14]}
        intensity={licht.accentSterkte}
        distance={58}
        color={licht.accentKleur}
      />

      {/* Koel daglicht dat door de dockdeuren naar binnen valt. */}
      {licht.daklichten && (
        <pointLight position={[0, 3, -HAL.diepte * 0.8]} intensity={18 * licht.daglicht} distance={26} color="#a9bddb" />
      )}
    </group>
  )
}
